import { useState, useCallback, useMemo, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";

interface LastOpenedEntry {
  projectId: string;
  openedAt: number;
}

const MAX_ENTRIES = 6;

function getStorageKey(userId: string) {
  return `last-opened-projects:${userId}`;
}

function readEntries(storageKey: string): LastOpenedEntry[] {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function useLastOpenedProjects() {
  const { user } = useAuth();
  const storageKey = user ? getStorageKey(user.id) : null;

  const [entries, setEntries] = useState<LastOpenedEntry[]>(() => {
    if (!storageKey) return [];
    return readEntries(storageKey);
  });

  // Re-initialize when user changes
  useEffect(() => {
    if (storageKey) {
      setEntries(readEntries(storageKey));
    } else {
      setEntries([]);
    }
  }, [storageKey]);

  const recordOpen = useCallback(
    (projectId: string) => {
      if (!storageKey) return;
      setEntries((prev) => {
        const filtered = prev.filter((e) => e.projectId !== projectId);
        const next = [{ projectId, openedAt: Date.now() }, ...filtered].slice(
          0,
          MAX_ENTRIES,
        );
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // Storage full or unavailable — silently degrade
        }
        return next;
      });
    },
    [storageKey],
  );

  const lastOpenedIds = useMemo(
    () => entries.map((e) => e.projectId),
    [entries],
  );

  return { recordOpen, lastOpenedIds };
}
